import React, { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Users, Search, Filter, RefreshCw, Eye, UserCircle, MoreVertical, X, Calendar, MapPin, Briefcase, Activity, Clock, AlertCircle, CheckCircle, XCircle} from 'lucide-react'
import SuperadminLayout from "../components/SuperadminLayout"

interface UserProfile {
  _id: string
  userId: string
  email: string
  fullName?: string
  role: string
  country?: string
  region?: string
  nclexStatus?: string
  accountStatus?: string
  createdAt?: string
  lastLoginAt?: string
  profilePhotoUrl?: string
  phoneNumber?: string
  specialty?: string
}

interface FilterState {
  role: string
  status: string
  country: string
  dateFrom: string
  dateTo: string
  searchQuery: string
}

export default function SuperadminUsersDirectory() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [users, setUsers] = useState<UserProfile[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "name" | "role">("newest")
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null)
  const [showFilters, setShowFilters] = useState(true)
  
  const usersPerPage = 20

  const [filters, setFilters] = useState<FilterState>({
    role: "",
    status: "",
    country: "",
    dateFrom: "",
    dateTo: "",
    searchQuery: ""
  })

  // Apply URL parameters on mount
  useEffect(() => {
    const filterParam = searchParams.get('filter')
    const roleParam = searchParams.get('role')
    
    if (filterParam === '24h') {
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
      setFilters(prev => ({
        ...prev,
        dateFrom: yesterday.toISOString().split('T')[0],
        role: roleParam === 'all' ? '' : (roleParam || '')
      }))
    }
  }, [searchParams])

  useEffect(() => {
    fetchUsers()
  }, [currentPage, sortBy])

  useEffect(() => {
    applyFilters()
  }, [users, filters])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      console.log("[Users] Starting fetch...")
      console.log("[Users] Auth user:", lumi.auth.user)
      console.log("[Users] Auth token:", lumi.auth.accessToken ? "Present" : "Missing")
      
      const result = await lumi.entities.UserProfiles.list({
        limit: 1000, // Fetch more for client-side filtering
        sort: getSortConfig()
      })

      console.log("[Users] Fetch successful!")
      console.log("[Users] Total users:", result.total)
      console.log("[Users] Users fetched:", result.list.length)
      console.log("[Users] Sample user:", result.list[0])
      console.log("[Users] All users:", result.list)

      setUsers(result.list)
      setTotalUsers(result.total)
    } catch (error) {
      console.error("[Users] Error fetching users:", error)
      toast.error("Failed to load users")
    } finally {
      setLoading(false)
    }
  }

  const getSortConfig = () => {
    switch (sortBy) {
      case "newest":
        return { _id: -1 }
      case "oldest":
        return { _id: 1 }
      case "name":
        return { fullName: 1 }
      case "role":
        return { role: 1 }
      default:
        return { _id: -1 }
    }
  }

  const applyFilters = () => {
    console.log("[Users] Applying filters...")
    console.log("[Users] Total users before filter:", users.length)
    console.log("[Users] Current filters:", filters)
    
    let filtered = [...users]

    // Search query
    if (filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase()
      filtered = filtered.filter(user =>
        user.email?.toLowerCase().includes(query) ||
        user.fullName?.toLowerCase().includes(query) ||
        user.userId?.toLowerCase().includes(query) ||
        user._id?.toLowerCase().includes(query)
      )
      console.log("[Users] After search filter:", filtered.length)
    }

    // Role filter
    if (filters.role) {
      filtered = filtered.filter(user => user.role === filters.role)
      console.log("[Users] After role filter:", filtered.length)
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(user => user.accountStatus === filters.status)
      console.log("[Users] After status filter:", filtered.length)
    }

    // Country filter
    if (filters.country) {
      filtered = filtered.filter(user => user.country === filters.country)
      console.log("[Users] After country filter:", filtered.length)
    }

    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom)
      console.log("[Users] Date from filter:", filters.dateFrom, "parsed:", fromDate)
      const beforeCount = filtered.length
      filtered = filtered.filter(user => {
        if (!user.createdAt) return false
        const userDate = new Date(user.createdAt)
        console.log("[Users] Checking user:", user.email, "createdAt:", user.createdAt, "parsed:", userDate, "passes:", userDate >= fromDate)
        return userDate >= fromDate
      })
      console.log("[Users] After dateFrom filter:", filtered.length, "(removed", beforeCount - filtered.length, ")")
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo)
      toDate.setHours(23, 59, 59, 999)
      console.log("[Users] Date to filter:", filters.dateTo, "parsed:", toDate)
      filtered = filtered.filter(user => {
        if (!user.createdAt) return false
        return new Date(user.createdAt) <= toDate
      })
      console.log("[Users] After dateTo filter:", filtered.length)
    }

    console.log("[Users] Final filtered count:", filtered.length)
    setFilteredUsers(filtered)
    setCurrentPage(1)
  }

  const handleImpersonate = async (user: UserProfile) => {
    try {
      const sessionToken = `imp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      // Create impersonation session in database
      await lumi.entities.ImpersonationSessions.create({
        session_token: sessionToken,
        superadmin_id: lumi.auth.user?.userId || "",
        superadmin_email: lumi.auth.user?.email || "",
        viewed_user_id: user.userId,
        viewed_user_email: user.email,
        viewed_role: user.role,
        entered_at: new Date().toISOString(),
        status: "active",
        ip_address: null,
        user_agent: navigator.userAgent
      })

      // Store in sessionStorage
      sessionStorage.setItem("impersonation_session", JSON.stringify({
        sessionToken,
        superadminId: lumi.auth.user?.userId,
        superadminEmail: lumi.auth.user?.email,
        viewedUserId: user.userId,
        viewedUserEmail: user.email,
        viewedUserName: user.fullName || user.email,
        viewedRole: user.role,
        enteredAt: new Date().toISOString()
      }))

      // Navigate to user's dashboard
      const roleRoutes: Record<string, string> = {
        nurse: "/dashboard/nurse",
        recruiter: "/dashboard/recruiter",
        affiliate: "/dashboard/affiliate",
        admin: "/dashboard/admin",
        superadmin: "/dashboard/superadmin"
      }

      toast.success(`Now viewing as ${user.fullName || user.email}`)
      navigate(roleRoutes[user.role] || "/dashboard/nurse")
    } catch (error) {
      console.error("Impersonation error:", error)
      toast.error("Failed to start impersonation session")
    }
  }

  const handleOpenProfile = (user: UserProfile) => {
    // Store user ID for viewing (without impersonation)
    sessionStorage.setItem("viewedUserId", user.userId)
    
    const roleRoutes: Record<string, string> = {
      nurse: "/dashboard/nurse",
      recruiter: "/dashboard/recruiter",
      affiliate: "/dashboard/affiliate",
      admin: "/dashboard/admin"
    }

    window.open(roleRoutes[user.role] || "/dashboard/nurse", "_blank")
  }

  const resetFilters = () => {
    setFilters({
      role: "",
      status: "",
      country: "",
      dateFrom: "",
      dateTo: "",
      searchQuery: ""
    })
  }

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      superadmin: "bg-amber-100 text-amber-800 border-amber-300",
      admin: "bg-purple-100 text-purple-800 border-purple-300",
      recruiter: "bg-blue-100 text-blue-800 border-blue-300",
      affiliate: "bg-green-100 text-green-800 border-green-300",
      nurse: "bg-teal-100 text-teal-800 border-teal-300"
    }
    return colors[role] || "bg-gray-100 text-gray-800 border-gray-300"
  }

  const getStatusBadgeColor = (status?: string) => {
    if (!status) return "bg-gray-100 text-gray-600"
    const colors: Record<string, string> = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-yellow-100 text-yellow-800",
      suspended: "bg-red-100 text-red-800"
    }
    return colors[status] || "bg-gray-100 text-gray-600"
  }

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  )

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage)

  const countries = ["Pakistan", "India", "Philippines", "Kuwait", "Saudi Arabia", "UAE", "Qatar", "Bahrain", "Oman", "United States", "United Kingdom", "Canada", "Australia"]

  return (
    <SuperadminLayout activeMenuItem="users">
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-amber-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Users</h1>
              <p className="text-sm text-gray-600">
                {filteredUsers.length} users {filters.role && `• Filtered by ${filters.role}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? "Hide" : "Show"} Filters
            </button>
            <button
              onClick={fetchUsers}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Filters</h3>
              <button
                onClick={resetFilters}
                className="text-sm text-amber-600 hover:text-amber-700 font-medium"
              >
                Reset to Default
              </button>
            </div>

            {/* Row 1: Search */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={filters.searchQuery}
                  onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                  placeholder="Search by name, email, or ID..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
            </div>

            {/* Row 2: Role, Status, Country */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={filters.role}
                  onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Roles</option>
                  <option value="nurse">Nurse</option>
                  <option value="recruiter">Recruiter</option>
                  <option value="affiliate">Affiliate</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <select
                  value={filters.country}
                  onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Countries</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: Date Range & Sort */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Joined From</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Joined To</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="name">Name (A-Z)</option>
                  <option value="role">Role</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No users found matching your filters</p>
              <button
                onClick={resetFilters}
                className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Country</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Joined</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedUsers.map(user => (
                      <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {user.profilePhotoUrl ? (
                              <img src={user.profilePhotoUrl} alt="" className="w-10 h-10 rounded-full" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-semibold">
                                {(user.fullName || user.email).charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-gray-900">{user.fullName || "—"}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeColor(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {user.country || "—"}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(user.accountStatus)}`}>
                            {user.accountStatus || "active"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleImpersonate(user)}
                              className="px-3 py-1 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-xs font-medium flex items-center gap-1"
                              title="View As (Impersonate)"
                            >
                              <Eye className="w-3 h-3" />
                              View As
                            </button>
                            <button
                              onClick={() => handleOpenProfile(user)}
                              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs font-medium flex items-center gap-1"
                              title="Open Profile"
                            >
                              <UserCircle className="w-3 h-3" />
                              Profile
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden divide-y divide-gray-200">
                {paginatedUsers.map(user => (
                  <div key={user._id} className="p-4">
                    <div className="flex items-start gap-3 mb-3">
                      {user.profilePhotoUrl ? (
                        <img src={user.profilePhotoUrl} alt="" className="w-12 h-12 rounded-full" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-semibold">
                          {(user.fullName || user.email).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{user.fullName || "—"}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeColor(user.role)}`}>
                            {user.role}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(user.accountStatus)}`}>
                            {user.accountStatus || "active"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {user.country || "—"}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleImpersonate(user)}
                        className="flex-1 px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium flex items-center justify-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        View As
                      </button>
                      <button
                        onClick={() => handleOpenProfile(user)}
                        className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium flex items-center justify-center gap-1"
                      >
                        <UserCircle className="w-4 h-4" />
                        Profile
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="border-t border-gray-200 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Showing {(currentPage - 1) * usersPerPage + 1} to {Math.min(currentPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        Previous
                      </button>
                      <span className="px-4 py-2 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Security Notice */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900">Security & Compliance</p>
              <p className="text-sm text-amber-700 mt-1">
                All impersonation sessions are logged and expire after 1 hour. Sessions are stored in ImpersonationSessions collection. 
                Use "View As" only when necessary for support or troubleshooting.
              </p>
            </div>
          </div>
        </div>
      </div>
    </SuperadminLayout>
  )
}
